import { useState, useEffect, useMemo } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { 
  Folder, 
  FolderPlus, 
  FolderOpen, 
  Edit2, 
  Trash2,
  ChevronRight,
  ChevronDown,
  FileText
} from 'lucide-react'
import { useFolderStore, type Folder as FolderType } from '../../../../stores/folderStore'
import type { SavedSkill } from '@creanizer/types'

interface FolderManagerProps {
  generatorType: string
  items: SavedSkill[]
  selectedFolderId: string | null
  onFolderSelect: (folderId: string | null) => void
  onItemMove?: (itemId: string, toFolderId: string | null) => void
  draggedItemId?: string | null
}

export function FolderManager({ 
  generatorType, 
  items, 
  selectedFolderId, 
  onFolderSelect,
  onItemMove,
  draggedItemId
}: FolderManagerProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderParentId, setNewFolderParentId] = useState<string | undefined>(undefined)
  const [isSubfolderCreation, setIsSubfolderCreation] = useState(false)
  const [editingFolder, setEditingFolder] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null)
  
  const {
    getFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    getItemsInFolder,
    moveItemBetweenFolders,
    cleanupOrphanedItems,
    getChildFolders,
    getFolderDepth,
    canCreateSubfolder,
    getFolderPath
  } = useFolderStore()
  
  const folders = getFolders(generatorType)
  const rootFolders = getChildFolders(generatorType, undefined)
  
  // 有効なアイテムIDリストをメモ化
  const validItemIds = useMemo(() => items.map(item => item.id), [items])
  
  // フォルダ深度情報をメモ化
  const folderDepths = useMemo(() => {
    const depths: Record<string, number> = {}
    folders.forEach(folder => {
      try {
        depths[folder.id] = getFolderDepth(generatorType, folder.id)
      } catch (error) {
        console.error('Error calculating depth for folder:', folder.id, error)
        depths[folder.id] = 0
      }
    })
    return depths
  }, [folders, generatorType, getFolderDepth])
  
  // 親フォルダ選択用のリストをメモ化
  const parentFolderOptions = useMemo(() => {
    return folders.filter(f => {
      const depth = folderDepths[f.id] || 0
      return depth < 2
    })
  }, [folders, folderDepths])
  
  // 削除されたアイテムをクリーンアップ（useEffectで実行）
  useEffect(() => {
    if (items.length === 0) return
    cleanupOrphanedItems(generatorType, validItemIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, generatorType])
  
  // 各フォルダのアイテム数を計算
  const getItemCount = (folderId: string | null) => {
    if (folderId === null) {
      // 未整理: どのフォルダにも属していないアイテムを計算
      const allFolderItemIds = new Set<string>()
      folders.forEach(folder => {
        getItemsInFolder(generatorType, folder.id).forEach(id => allFolderItemIds.add(id))
      })
      return validItemIds.filter(id => !allFolderItemIds.has(id)).length
    }
    
    const itemIds = getItemsInFolder(generatorType, folderId)
    return itemIds.filter(id => validItemIds.includes(id)).length
  }
  
  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      try {
        createFolder(generatorType, newFolderName.trim(), newFolderParentId)
        setNewFolderName('')
        setNewFolderParentId(undefined)
        setIsSubfolderCreation(false)
        setIsCreating(false)
      } catch (error) {
        console.error('Error creating folder:', error)
        alert('フォルダの作成中にエラーが発生しました')
      }
    }
  }
  
  const handleEditFolder = (folderId: string, newName: string) => {
    if (newName.trim()) {
      updateFolder(generatorType, folderId, { name: newName.trim() })
    }
    setEditingFolder(null)
    setEditingName('')
  }
  
  const handleDeleteFolder = (folderId: string) => {
    if (confirm('フォルダを削除しますか？\n中のアイテムは「未整理」に移動します。')) {
      deleteFolder(generatorType, folderId, true)
      if (selectedFolderId === folderId) {
        onFolderSelect(null)
      }
    }
  }
  
  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }
  
  // フォルダ名を省略表示する関数
  const truncateFolderName = (name: string, maxLength: number = 7) => {
    if (name.length <= maxLength) return name
    return `${name.slice(0, maxLength)}...`
  }

  // フォルダを再帰的に表示
  const renderFolder = (folder: FolderType, depth: number = 0) => {
    const itemCount = getItemCount(folder.id)
    const isEditing = editingFolder === folder.id
    const childFolders = getChildFolders(generatorType, folder.id)
    const hasChildren = childFolders.length > 0
    const isExpanded = expandedFolders.has(folder.id)
    const folderDepth = folderDepths[folder.id] || 0
    const canHaveSubfolder = folderDepth < 2
    
    return (
      <div key={folder.id}>
        <div
          className={`px-2 py-1.5 rounded-md hover:bg-accent ${
            selectedFolderId === folder.id ? 'bg-accent' : ''
          } ${
            dragOverFolderId === folder.id && draggedItemId ? 'ring-2 ring-primary bg-primary/10' : ''
          }`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onDragOver={(e) => {
            if (draggedItemId && onItemMove) {
              e.preventDefault()
              e.stopPropagation()
              setDragOverFolderId(folder.id)
            }
          }}
          onDragEnter={(e) => {
            if (draggedItemId && onItemMove) {
              e.preventDefault()
              e.stopPropagation()
              setDragOverFolderId(folder.id)
            }
          }}
          onDragLeave={(e) => {
            e.stopPropagation()
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDragOverFolderId(null)
            }
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (draggedItemId && onItemMove) {
              onItemMove(draggedItemId, folder.id)
              setDragOverFolderId(null)
            }
          }}
        >
          <button
            onClick={() => {
              onFolderSelect(folder.id)
              if (hasChildren) {
                toggleFolder(folder.id)
              }
            }}
            className="w-full text-left"
          >
            <div className="flex items-center gap-2">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleFolder(folder.id)
                  }}
                  className="p-0"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
              )}
              {!hasChildren && <div className="w-3" />}
              <Folder className="h-4 w-4" style={{ color: folder.color }} />
              {isEditing ? (
                <Input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleEditFolder(folder.id, editingName)
                    } else if (e.key === 'Escape') {
                      setEditingFolder(null)
                      setEditingName('')
                    }
                  }}
                  onBlur={() => handleEditFolder(folder.id, editingName)}
                  className="h-6 text-sm flex-1"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="text-sm" title={folder.name}>
                  {truncateFolderName(folder.name)}
                </span>
              )}
              <span className="text-xs text-muted-foreground">({itemCount})</span>
            </div>
          </button>
          
          {!isEditing && (
            <div className="flex gap-1 mt-1" style={{ paddingLeft: '19px' }}>
              {canHaveSubfolder && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    setNewFolderParentId(folder.id)
                    setIsSubfolderCreation(true)
                    setIsCreating(true)
                  }}
                  title="サブフォルダを作成"
                >
                  <FolderPlus className="h-3 w-3" />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingFolder(folder.id)
                  setEditingName(folder.name)
                }}
                title="フォルダ名を編集"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteFolder(folder.id)
                }}
                title="フォルダを削除"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        
        {/* 子フォルダを表示 */}
        {isExpanded && childFolders.map(childFolder => 
          renderFolder(childFolder, depth + 1)
        )}
      </div>
    )
  }
  
  const uncategorizedCount = getItemCount(null)
  const totalCount = items.length
  
  return (
    <div className="space-y-2">
      {/* フォルダ作成ボタン */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-semibold">フォルダ</h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setNewFolderParentId(undefined)
            setIsSubfolderCreation(false)
            setIsCreating(true)
          }}
        >
          <FolderPlus className="h-4 w-4" />
        </Button>
      </div>
      
      {/* 全て表示 */}
      <button
        onClick={() => onFolderSelect('all')}
        className={`w-full text-left px-2 py-1.5 rounded-md hover:bg-accent flex items-center justify-between ${
          selectedFolderId === 'all' ? 'bg-accent' : ''
        }`}
      >
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <span className="text-sm">すべて</span>
        </div>
        <span className="text-xs text-muted-foreground">{totalCount}</span>
      </button>
      
      {/* ルートフォルダ一覧 */}
      {rootFolders.map(folder => renderFolder(folder, 0))}
      
      {/* 未整理 */}
      <button
        onClick={() => onFolderSelect(null)}
        className={`w-full text-left px-2 py-1.5 rounded-md hover:bg-accent flex items-center justify-between ${
          selectedFolderId === null ? 'bg-accent' : ''
        } ${
          dragOverFolderId === 'uncategorized' && draggedItemId ? 'ring-2 ring-primary bg-primary/10' : ''
        }`}
        onDragOver={(e) => {
          if (draggedItemId && onItemMove) {
            e.preventDefault()
            e.stopPropagation()
            setDragOverFolderId('uncategorized')
          }
        }}
        onDragEnter={(e) => {
          if (draggedItemId && onItemMove) {
            e.preventDefault()
            e.stopPropagation()
            setDragOverFolderId('uncategorized')
          }
        }}
        onDragLeave={(e) => {
          e.stopPropagation()
          setDragOverFolderId(null)
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (draggedItemId && onItemMove) {
            onItemMove(draggedItemId, null)
            setDragOverFolderId(null)
          }
        }}
      >
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4" />
          <span className="text-sm">未整理</span>
        </div>
        <span className="text-xs text-muted-foreground">{uncategorizedCount}</span>
      </button>
      
      {/* フォルダ作成ダイアログ */}
      <Dialog open={isCreating} onOpenChange={(open) => {
        setIsCreating(open)
        if (!open) {
          setNewFolderName('')
          setNewFolderParentId(undefined)
          setIsSubfolderCreation(false)
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新しいフォルダを作成</DialogTitle>
            <DialogDescription>
              スキルを整理するためのフォルダを作成します
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="フォルダ名"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder()
                }
              }}
              autoFocus
            />
            
            {/* 親フォルダ選択（サブフォルダ作成モードでない時のみ表示） */}
            {!isSubfolderCreation && folders.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">親フォルダ（オプション）</label>
                <Select
                  value={newFolderParentId === undefined ? 'root' : newFolderParentId}
                  onValueChange={(value) => {
                    setNewFolderParentId(value === 'root' ? undefined : value)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ルート" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">ルート（最上位）</SelectItem>
                    {parentFolderOptions.map(folder => {
                      const depth = folderDepths[folder.id] || 0
                      return (
                        <SelectItem key={folder.id} value={folder.id}>
                          {'　'.repeat(depth)}{folder.name}
                          {depth === 0 && ' (階層1まで作成可)'}
                          {depth === 1 && ' (階層2まで作成可)'}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreating(false)}>
                キャンセル
              </Button>
              <Button onClick={handleCreateFolder}>
                作成
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// アイテム移動用のセレクトコンポーネント
export function FolderMoveSelect({ 
  generatorType, 
  itemId,
  currentFolderId,
  onMove 
}: {
  generatorType: string
  itemId: string
  currentFolderId: string | null | undefined
  onMove: (toFolderId: string | null) => void
}) {
  const { getFolders, moveItemBetweenFolders, getItemFolder, getChildFolders } = useFolderStore()
  const folders = getFolders(generatorType)
  const currentFolder = currentFolderId ? getItemFolder(generatorType, itemId) : null
  
  const handleMove = (value: string) => {
    const toFolderId = value === 'uncategorized' ? null : value
    moveItemBetweenFolders(generatorType, itemId, currentFolder?.id || null, toFolderId)
    onMove(toFolderId)
  }
  
  // 階層構造を保った形でフォルダを整理
  const buildFolderTree = () => {
    const result: JSX.Element[] = []
    
    // ルートフォルダから再帰的に構築
    const renderFolderOptions = (parentId: string | undefined, depth: number = 0) => {
      const childFolders = getChildFolders(generatorType, parentId)
      childFolders.forEach(folder => {
        result.push(
          <SelectItem key={folder.id} value={folder.id}>
            {'　'.repeat(depth)}{folder.name}
          </SelectItem>
        )
        // 子フォルダを再帰的に処理
        renderFolderOptions(folder.id, depth + 1)
      })
    }
    
    // ルートフォルダから開始
    renderFolderOptions(undefined, 0)
    
    return result
  }
  
  return (
    <Select 
      value={currentFolder?.id || 'uncategorized'} 
      onValueChange={handleMove}
    >
      <SelectTrigger className="h-7 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="uncategorized">未整理</SelectItem>
        {buildFolderTree()}
      </SelectContent>
    </Select>
  )
}